import React from 'react';
import './OrdersTable.css';
import Pagination from './Pagination';
import useUrlDetails from '../hooks/useUrlDetails';

const BanksTable = ({ data, pagination, onPageChange }) => {

  // Extract data - support both the new paginated format and the old format
  let banks = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    banks = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    banks = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      banks = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      banks = firstOp.data.list;
    }
  }

  const [selectedBank, setSelectedBank] = useUrlDetails(banks, 'bankId');

  // Sort by creation date (create_time) - newest first
  banks.sort((a, b) => {
    const timeA = a.create_time || 0;
    const timeB = b.create_time || 0;
    return timeB - timeA;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>{day}.{month}.{year}</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Get all unique keys from the data for dynamic display
  const getAllKeys = () => {
    const keysSet = new Set();
    banks.forEach(bank => {
      Object.keys(bank).forEach(key => {
        // Exclude service fields
        if (key !== 'ref' && key !== 'create_time' && key !== 'change_time') {
          keysSet.add(key);
        }
      });
    });
    return Array.from(keysSet);
  };

  // Determine the columns to display
  const allKeys = getAllKeys();
  const displayColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (bank) => {
        const created = bank.create_time || null;
        return formatDate(created);
      }
    },
    ...allKeys.map(key => ({
      key,
      label: key,
      getValue: (bank) => {
        const value = bank[key];
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      }
    }))
  ];

  if (banks.length === 0) {
    return (
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Banks List (0)</h3>
        </div>
        <div className="page-placeholder">
          <p>No banks found</p>
        </div>
      </div>
    );
  }

  const totalItems = pagination?.total || banks.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Banks List ({totalItems})</h3>
        </div>
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {banks.map((bank, index) => (
                <tr 
                  key={bank.ref || bank.id || index}
                  className="order-row"
                  onClick={() => setSelectedBank(bank)}
                >
                  {displayColumns.map((column) => (
                    <td 
                      key={column.key} 
                      className={`column-${column.key.toLowerCase()}`}
                      data-label={column.label}
                    >
                      {column.getValue(bank)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedBank && (
        <div className="record-preview-overlay" onClick={() => setSelectedBank(null)}>
          <div className="record-preview-card" onClick={(e) => e.stopPropagation()}>
            <h2>Bank Details</h2>
            <pre>{JSON.stringify(selectedBank, null, 2)}</pre>
            <div className="record-preview-actions">
              <button className="add-button" onClick={() => setSelectedBank(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BanksTable;
